import { type FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageTitle } from "../../../../components/PageTitle";
import {
  useDeleteGroupApi,
  useGroupApi,
  useRemoveContactFromGroupApi,
} from "../../../../hooks/use-distribution-api";

export const GroupDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: group,
    loading,
    error,
    refetch,
  } = useGroupApi({
    id: id || "",
    skip: !id,
  });
  const { deleteGroup } = useDeleteGroupApi();
  const { removeContact } = useRemoveContactFromGroupApi();

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [removeContactConfirm, setRemoveContactConfirm] = useState<
    string | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleDeleteGroup = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await deleteGroup(id);
      navigate("/admin/distribution/groups");
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveContact = async (contactId: string) => {
    if (!id) return;
    setActionLoading(true);
    try {
      await removeContact(id, contactId);
      setRemoveContactConfirm(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>グループ詳細</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
        <Link to="/admin/distribution/groups" className="btn btn-ghost mt-4">
          ← 一覧に戻る
        </Link>
      </div>
    );
  }

  if (loading || !group) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>グループ詳細</PageTitle>
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/distribution/groups"
            className="btn btn-ghost btn-sm"
          >
            ← 戻る
          </Link>
          <PageTitle>{group.name}</PageTitle>
          {group.isDefault && (
            <span className="badge badge-warning">デフォルト</span>
          )}
        </div>
        <div className="flex gap-2">
          <Link
            to={`/admin/distribution/groups/${id}/edit`}
            className="btn btn-primary btn-sm"
          >
            編集
          </Link>
          <button
            type="button"
            onClick={() => setDeleteConfirm(true)}
            className="btn btn-error btn-sm"
          >
            削除
          </button>
        </div>
      </div>

      {/* 基本情報 */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-lg">基本情報</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <div className="text-sm text-base-content/60">グループ名</div>
              <div className="font-medium">{group.name}</div>
            </div>
            <div>
              <div className="text-sm text-base-content/60">説明</div>
              <div>{group.description || "-"}</div>
            </div>
            <div>
              <div className="text-sm text-base-content/60">作成者</div>
              <div>{group.createdBy.name}</div>
            </div>
            <div>
              <div className="text-sm text-base-content/60">作成日</div>
              <div>{new Date(group.createdAt).toLocaleString("ja-JP")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* 連絡先一覧 */}
      <div className="card bg-base-100 shadow-md">
        <div className="card-body">
          <div className="flex justify-between items-center">
            <h2 className="card-title text-lg">
              連絡先 ({group.contacts.length}件)
            </h2>
            <Link
              to={`/admin/distribution/groups/${id}/add-contacts`}
              className="btn btn-primary btn-sm"
            >
              + 連絡先を追加
            </Link>
          </div>

          {group.contacts.length === 0 ? (
            <div className="text-center py-8 text-base-content/60">
              連絡先がありません
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="table">
                <thead>
                  <tr>
                    <th>会社名</th>
                    <th>担当者名</th>
                    <th>メールアドレス</th>
                    <th>状態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {group.contacts.map(contact => (
                    <tr key={contact.id} className="hover">
                      <td>
                        <Link
                          to={`/admin/distribution/contacts/${contact.id}`}
                          className="link link-primary"
                        >
                          {contact.companyName}
                        </Link>
                      </td>
                      <td>{contact.contactName || "-"}</td>
                      <td>{contact.email}</td>
                      <td>
                        <span
                          className={`badge badge-sm ${
                            contact.isActive ? "badge-success" : "badge-error"
                          }`}
                        >
                          {contact.isActive ? "有効" : "無効"}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => setRemoveContactConfirm(contact.id)}
                          className="btn btn-ghost btn-xs text-error"
                        >
                          解除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* グループ削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">グループを削除しますか？</h3>
            <p className="py-4">
              この操作は取り消せません。グループ内の連絡先との紐付けも削除されます。
            </p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteConfirm(false)}
                disabled={actionLoading}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn btn-error"
                onClick={handleDeleteGroup}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "削除"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setDeleteConfirm(false)}
          />
        </div>
      )}

      {/* 連絡先解除確認モーダル */}
      {removeContactConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              連絡先をグループから解除しますか？
            </h3>
            <p className="py-4">連絡先自体は削除されません。</p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setRemoveContactConfirm(null)}
                disabled={actionLoading}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn btn-error"
                onClick={() => handleRemoveContact(removeContactConfirm)}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "解除"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setRemoveContactConfirm(null)}
          />
        </div>
      )}
    </div>
  );
};
